import { AvailabilityType } from '@depanneo/types'
import type { TechnicianFormData } from '@/shared/types/technicians'
import { vacationEndBound } from '../../shared/vacation-window'

export function transformTechnicianData(data: TechnicianFormData) {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    region,
    addressLine1,
    workHours,
    vacationPeriods,
    ownerContact,
    operationalContact,
    operationalSameAsOwner,
    secondaryContacts,
    billingContact,
    billingSameAsOwner,
    ...restData
  } = data

  // Clean billingAddress - remove extra fields
  const cleanBillingAddress = data.billingAddress
    ? {
        useOperationalAddress:
          data.billingAddress.useOperationalAddress === 'true',
        ...(data.billingAddress.useOperationalAddress === 'false'
          ? {
              addressLine1: data.billingAddress.addressLine1,
              addressLine2: data.billingAddress.addressLine2,
              city: data.billingAddress.city,
              postalCode: data.billingAddress.postalCode,
              country: data.billingAddress.country,
              gmapsPlaceId: data.billingAddress.gmapsPlaceId,
            }
          : {}),
      }
    : undefined

  return {
    ...restData,
    address: addressLine1,
    // Ensure department is max 3 chars
    department: restData.department?.substring(0, 3),
    billingAddress: cleanBillingAddress,
    availability: [
      ...(workHours
        ?.filter((wh) => wh.timeStart && wh.timeEnd)
        .map((wh) => ({
          availabilityType: AvailabilityType.WEEKLY_SCHEDULE,
          dayOfWeek: wh.dayOfWeek,
          timeStart:
            wh.timeStart?.length === 8 ? wh.timeStart : `${wh.timeStart}:00`,
          timeEnd: wh.timeEnd?.length === 8 ? wh.timeEnd : `${wh.timeEnd}:00`,
          is247: wh.is247,
        })) || []),
      ...(vacationPeriods
        ?.filter((vp) => vp.unavailableFrom && vp.unavailableTo)
        .map((vp) => ({
          availabilityType: AvailabilityType.UNAVAILABILITY_PERIOD,
          unavailableFrom: vp.unavailableFrom,
          unavailableTo: vacationEndBound(vp.unavailableTo),
          unavailabilityReason: vp.unavailabilityReason,
        })) || []),
    ],
    managerContact: ownerContact
      ? {
          firstName: ownerContact.firstName,
          lastName: ownerContact.lastName,
          email: ownerContact.email,
          phone: ownerContact.mobilePhone,
        }
      : undefined,
    operationalContact: {
      sameAsManager: operationalSameAsOwner === 'true',
      contact:
        operationalSameAsOwner === 'false' && operationalContact
          ? {
              firstName: operationalContact.firstName,
              lastName: operationalContact.lastName,
              email: operationalContact.email,
              phone: operationalContact.mobilePhone,
            }
          : undefined,
    },
    secondaryContacts: secondaryContacts?.map((c) => ({
      firstName: c.firstName,
      lastName: c.lastName,
      email: c.email,
      phone: c.mobilePhone,
      contactRoles: [c.role],
    })),
    billingContact:
      billingSameAsOwner === 'false' && billingContact
        ? {
            firstName: billingContact.firstName,
            lastName: billingContact.lastName,
            email: billingContact.email,
            phone: billingContact.mobilePhone,
            ...(billingContact.landlinePhone && {
              landline: billingContact.landlinePhone,
            }),
          }
        : ownerContact
          ? {
              firstName: ownerContact.firstName,
              lastName: ownerContact.lastName,
              email: ownerContact.email,
              phone: ownerContact.mobilePhone,
              ...(ownerContact.landlinePhone && {
                landline: ownerContact.landlinePhone,
              }),
            }
          : undefined,
  }
}
